import { observable, computed, action, toJS } from 'mobx';
import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';

import { Platform } from 'react-native';
import { uniq } from 'lodash';

import { signInAndFetchDataFB, signIn, authCheck, signOut, getIdToken } from '../lib/userApiCalls';

import { getWarrior, onUserStatusChanged } from '../lib/dbCalls';

import { createWarrior, addLoginLog } from '../lib/apiCalls';
import appStore from './app';
import heroStore from './hero';
import bonusesStore from './bonuses';
import clansStore from './clans';
import adsStore from '../lib/ads';
import buildingData from './buildingData';
import { isAdmin } from '../utils/warriorUtils';

import { UserType } from '../lib/types';

import { VERSION, IS_WEB, ERRORS } from '../lib/appConstants';
import { t, setToStorage, removeFromStorage } from '../utils/appUtils';

export const SIGN_IN_TYPE_FB = 'signInTypeFB';
export const SIGN_IN_TYPE_EMAIL = 'signInTypeEmail';

export const LOGGED_IN = 'LOGGED_IN';

async function registerForPushNotificationsAsync() {
  let token;
  if (Device.isDevice) {
    const { status: existingStatus } = await Notifications.getPermissionsAsync();
    let finalStatus = existingStatus;
    if (existingStatus !== 'granted') {
      const { status } = await Notifications.requestPermissionsAsync();
      finalStatus = status;
    }
    if (finalStatus !== 'granted') {
      // alert('Failed to get push token for push notification!');
      return;
    }
    token = (await Notifications.getExpoPushTokenAsync()).data;
  } else {
    // alert('Must use physical device for Push Notifications');
  }

  return token;
}

export type InitDataType = {
  login: string;
  quick?: boolean;
};

async function heroExists(id: string): Promise<boolean> {
  const hero = await getWarrior(id);
  return Boolean(hero);
}

async function saveCommonData() {
  if (!IS_WEB) {
    heroStore.hero.version = VERSION;
  }
  let { platform }: { platform: any } = heroStore.hero;
  platform = !platform ? [] : platform.split(',');
  platform.push(Platform.OS);
  heroStore.hero.platform = uniq(platform).join(',');

  if (!IS_WEB && isAdmin(heroStore.hero)) {
    (heroStore.hero as any).pushToken = await registerForPushNotificationsAsync();
  }

  await heroStore.save(['version', 'platform', 'pushToken']);
}

class Auth {
  @observable
  user!: UserType;

  userStatusChangedRef: any = {};

  @action
  async init() {
    try {
      this.user = await authCheck();
    } catch (e) {
      await removeFromStorage(LOGGED_IN);
      return e ? (e as any).message : false;
    }

    await this.prepare();

    return undefined;
  }

  @computed
  get isLoggedIn(): boolean {
    return Boolean(this.user);
  }

  @action
  async signIn(email: string, password: string, data?: InitDataType) {
    try {
      this.user = await signIn(email, password);
    } catch (e) {
      if ([ERRORS.USER_NOT_FOUND, ERRORS.INVALID_EMAIL].includes((e as any).code)) {
        return t('auth.wrongEmailPassword');
      }
      return (e as any).message;
    }
    await setToStorage(LOGGED_IN, 'true');
    await this.prepare(data);

    return undefined;
  }

  @action
  async signInFB(data: InitDataType) {
    if (!data) {
      const result = await signInAndFetchDataFB();
      if (!result) return false;
      if ((result as any).message) return result;

      this.user = result;
      if (!(await heroExists(this.user.uid))) return { signUp: true };
    }

    await setToStorage(LOGGED_IN, 'true');
    await this.prepare(data);

    return undefined;
  }

  @action
  async signOut() {
    this.userStatusChangedRef.off();
    heroStore.clear();
    bonusesStore.clear();
    clansStore.clear();
    await heroStore.updateStatus(false);
    await removeFromStorage(LOGGED_IN);

    this.user = null as any;
    buildingData.clear();
    signOut();
  }

  @action
  async refreshIdToken() {
    this.user.idToken = await getIdToken(true);
  }

  async prepare(data?: InitDataType) {
    await appStore.fetchInitData();
    await adsStore.init();
    if (data) await this.initData(data);
    await heroStore.fetch(toJS(this.user));

    addLoginLog();
    bonusesStore.listenChanges(heroStore.hero.id);
    clansStore.listenChanges(heroStore.hero.id);

    if (heroStore.hero.login === 'StormFire' && process.env.NODE_ENV === 'production') {
      // cleanBotsFromCombat();
    }

    saveCommonData();

    this.listenToStatus();
  }

  async initData(data: InitDataType) {
    if (!(await heroExists(this.user.uid))) {
      await createWarrior(data.login, data.quick);
    }
  }

  getProvider() {
    return this.user.providerData[0].providerId === 'facebook.com'
      ? SIGN_IN_TYPE_FB
      : SIGN_IN_TYPE_EMAIL;
  }

  listenToStatus() {
    this.userStatusChangedRef = onUserStatusChanged(async (value: any) => {
      await heroStore.updateStatus(value);
    });
  }
}

export default new Auth();
